import {
    InvoiceDto,
    InvoiceListItemDto,
    InvoiceMutationDto,
    InvoiceType,
    LineItemDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
    LineItemType,
    MetaDataDto,
} from '../../../config/finance/invoiceConfig'
import { DataPromise } from '../../../context/AxiosContext'
import { useCallback, useMemo } from 'react'
import { EntityDto } from '../../../config/appConfig'
import { createLineItemMap, createValuesLineItemMap } from '../../../components/Form/Finance/Invoice/LineItemGroups'
import { DisbursementListItemDto, FixedPriceListItemDto, TimeListItemDto, TravelListItemDto } from '../../../config/registrationConfig'
import * as UUID from 'uuid'

import { PriceVat } from '../../../components/Form/Finance/Invoice/InvoiceForm'
import { formatTimeInMinutes, multiplyTime, parseNumber, parseTimeInMinutes } from '../../../helpers/numberHelper'
import { formatPriceVat, getPriceVat, reducePrices } from '../../../helpers/priceHelper'
import { PricePrecedence, QuantityDto, SurchargeListItemDto } from '../../../config/finance/financeConfig'
import useCurrency from '../../useCurrency'
import { SurchargeValueType, VatType } from '../../../config/i18nConfig'
import useSurchargeTypes from '../../useSurchargeTypes'
import useVatTypes from '../../useVatTypes'
import { IReferenceDto } from '../../../config/fileConfig'

export type LineItemEntity = EntityDto & {
    file: IReferenceDto
    date: string
}

export type LineItemEntities = {
    selection: Map<LineItemType, LineItemEntity[]> // All used line items by line item type.
    orphans: Map<LineItemType, LineItemEntity[]> // All unused line items by line item type.
    hasOrphans: boolean // Whether any unused line items still exist in this file.
    entityTypeMap: Map<string, LineItemType>
    entityMap: Map<string, LineItemEntity>
    map: (item: LineItemEntity) => LineItemMutationDto
    findQuantity: (itemId: string) => QuantityDto[]
    getPrice: (entity: LineItemEntity, defaultVatType: VatType) => PriceVat
    getGroupPrice: (group: LineItemGroupMutationDto, vat: VatType, recalculate?: boolean) => PriceVat
    getTotalTime: (entities: LineItemMutationDto[]) => string
    getTotalDistance: (entities: LineItemMutationDto[]) => string
    toSettlePrice: number
    getUpdatedDepositPrices: () => Map<string, PriceVat>
}

export default function useLineItemEntities(meta: DataPromise<MetaDataDto>, values?: InvoiceMutationDto, invoice?: InvoiceDto): LineItemEntities {
    const { getSurchargeType } = useSurchargeTypes()
    const { getVatType, defaultVatType } = useVatTypes()

    const selectedIds = useMemo(
        () =>
            (values?.lineItemGroups || []).flatMap(lineItemGroup => [
                ...lineItemGroup.lineItems.map(lineItem => lineItem.entityId),
                lineItemGroup.fixedPriceId,
            ]),
        [values, values?.lineItemGroups],
    )
    const selection = useMemo<Map<LineItemType, LineItemEntity[]>>(() => createLineItemMap(selectedIds, meta.data, true), [meta.data, selectedIds])
    const orphans = useMemo<Map<LineItemType, LineItemEntity[]>>(() => createLineItemMap(selectedIds, meta.data, false), [meta.data, selectedIds])
    const hasOrphans = useMemo<boolean>(() => [...orphans.values()].some(entities => entities.length > 0), [orphans])
    const [entityTypeMap, entityMap] = useMemo<[Map<string, LineItemType>, Map<string, LineItemEntity>]>(() => {
        const entities = [...createLineItemMap([], meta.data, false).entries()]
        return [
            new Map(entities.flatMap(([lineItemType, entities]) => entities.map(entity => [entity.id, lineItemType]))),
            new Map(entities.flatMap(([, entities]) => entities.map(entity => [entity.id, entity]))),
        ]
    }, [orphans, meta.data])
    const valuesMap = useMemo<Map<string, LineItemDto>>(() => {
        const entities = [...createValuesLineItemMap([], invoice?.lineItemGroups, false).entries()]
        return new Map(entities.flatMap(([, entities]) => entities.map(entity => [entity.entityId, entity])))
    }, [orphans, invoice])
    const { currency } = useCurrency(values?.currency)

    const map = useCallback(
        (item: LineItemEntity): LineItemMutationDto => ({
            id: UUID.v4(),
            type: entityTypeMap.get(item.id),
            entityId: item.id,
        }),
        [entityTypeMap],
    )

    const findQuantity = useCallback(
        (itemId: string): QuantityDto[] => {
            if (invoice) {
                const quantities = invoice?.lineItemGroups
                    ?.flatMap(group => group.lineItems)
                    ?.filter(lineItem => lineItem.entityId === itemId)
                    ?.map(lineItem => lineItem.quantity)

                return quantities?.length > 0 ? quantities : undefined
            }

            return undefined
        },
        [invoice],
    )

    const getPrice = useCallback(
        (item: LineItemEntity | LineItemMutationDto, defaultVatType: VatType): PriceVat => {
            if (!item) {
                return { subtotal: null, vat: [], total: null }
            }
            switch (entityTypeMap.get(item.id) || item['type']) {
                case LineItemType.Disbursement: {
                    const disbursementItem: Partial<DisbursementListItemDto> =
                        (findQuantity(item.id) ?? findQuantity(item['entityId']))?.map(quantity => ({
                            ...(item as DisbursementListItemDto),
                            distance: quantity.amount,
                            rate: { amount: quantity.rate, currency: currency?.alpha },
                        }))?.[0] || (item as DisbursementListItemDto)

                    return formatPriceVat(
                        parseNumber(disbursementItem?.rate?.amount || 0, 'en'),
                        getVatType(disbursementItem?.vatType) || defaultVatType,
                        currency,
                    )
                }
                case LineItemType.FixedPrice: {
                    const listItem = item as FixedPriceListItemDto
                    return formatPriceVat(parseNumber(listItem?.rate?.amount || 0, 'en'), getVatType(listItem.vatType) || defaultVatType, currency)
                }
                case LineItemType.Invoice: {
                    const listItem = item as InvoiceListItemDto
                    if (listItem?.type !== InvoiceType.Deposit) {
                        if (!listItem?.price) {
                            return { subtotal: 0, vat: [], total: 0 }
                        }

                        return {
                            subtotal: parseNumber(listItem.price.subtotal, 'en') * -1,
                            vat: (listItem.price.vat || []).map(vat => ({
                                value: parseNumber(vat.value, 'en') * -1,
                                type: vat.type,
                            })),
                            total: parseNumber(listItem.price.total, 'en') * -1,
                        }
                    }

                    const depositPrices = getUpdatedDepositPrices()
                    const invoicePrice = depositPrices.get(item.id)

                    return invoicePrice ?? { subtotal: 0, vat: [], total: 0 }
                }
                case LineItemType.Surcharge: {
                    const listItem = item as SurchargeListItemDto
                    const surchargeType = getSurchargeType(listItem?.surchargeType)
                    if (surchargeType?.valueType === SurchargeValueType.Percentage && !!surchargeType?.prefillType) {
                        return selectedIds
                            .filter(id => listItem.file?.id === entityMap.get(id)?.file?.id)
                            .filter(id => surchargeType.prefillType.includes(entityTypeMap.get(id)))
                            .map(id => entityMap.get(id))
                            .map(entity => getPrice(entity, defaultVatType))
                            .map(price =>
                                formatPriceVat(
                                    (price.subtotal / 100) * parseNumber(listItem.value || 0, 'en'),
                                    getVatType(price.vat?.[0].type),
                                    currency,
                                ),
                            )
                            .reduce(reducePrices, {
                                subtotal: 0,
                                vat: [],
                                total: 0,
                            })
                    }

                    return formatPriceVat(parseNumber(listItem?.value || 0, 'en'), defaultVatType, currency)
                }
                case LineItemType.Time: {
                    const timeItem: Partial<TimeListItemDto> =
                        (findQuantity(item.id) ?? findQuantity(item['entityId']))?.map(quantity => ({
                            ...(item as TimeListItemDto),
                            duration: quantity.amount,
                            rate: { amount: quantity.rate, currency: currency?.alpha },
                        }))?.[0] || (item as TimeListItemDto)

                    return formatPriceVat(multiplyTime(timeItem?.duration, parseNumber(timeItem?.rate?.amount || 0, 'en')), defaultVatType, currency)
                }
                case LineItemType.Travel: {
                    const travelItem: Partial<TravelListItemDto> =
                        (findQuantity(item.id) ?? findQuantity(item['entityId']))?.map(quantity => ({
                            ...(item as TravelListItemDto),
                            distance: quantity.amount,
                            rate: { amount: quantity.rate, currency: currency?.alpha },
                        }))?.[0] || (item as TravelListItemDto)

                    return formatPriceVat(
                        parseNumber(travelItem?.distance, 'en') * parseNumber(travelItem?.rate?.amount || 0, 'en'),
                        defaultVatType,
                        currency,
                    )
                }
                default: {
                    return { subtotal: null, vat: [], total: null }
                }
            }
        },
        [entityTypeMap],
    )

    const getGroupPrice = useCallback(
        (group: LineItemGroupMutationDto, vat: VatType, recalculate?: boolean): PriceVat => {
            if (group.precedence === PricePrecedence.Inherited || recalculate) {
                return getPriceVat(
                    group.lineItems
                        .map(lineItem => getPrice(entityMap.get(lineItem.entityId) || lineItem, vat))
                        .reduce(reducePrices, {
                            subtotal: 0,
                            vat: [],
                            total: 0,
                        }).subtotal,
                    vat,
                    currency,
                )
            }

            return getPriceVat(parseNumber(group.price, 'en'), vat, currency, group.precedence)
        },
        [entityMap, getPrice],
    )

    const getTotalTime = useCallback(
        (entities: LineItemMutationDto[]): string => {
            return formatTimeInMinutes(
                entities
                    ?.filter(entity => entity.type === LineItemType.Time)
                    ?.map(
                        entity =>
                            (findQuantity(entity.id) ?? findQuantity(entity.entityId))?.[0]?.amount ||
                            (entityMap.get(entity.entityId) as TimeListItemDto)?.duration ||
                            '0:00',
                    )
                    ?.map(duration => parseTimeInMinutes(duration))
                    ?.reduce((a, b) => a + b, 0),
            )
        },
        [entityMap],
    )

    const getTotalDistance = useCallback(
        (entities: LineItemMutationDto[]): string => {
            return entities
                ?.filter(entity => entity.type === LineItemType.Travel)
                ?.map(
                    entity =>
                        (findQuantity(entity.id) ?? findQuantity(entity.entityId))?.[0]?.amount ||
                        (entityMap.get(entity.entityId) as TravelListItemDto)?.distance ||
                        '0',
                )
                ?.map(distance => parseInt(distance))
                ?.reduce((a, b) => a + b, 0)
                .toString()
        },
        [entityMap],
    )

    //Sum all line item groups that do not contain an invoice
    const toSettlePrice = useMemo(() => {
        const result = values?.lineItemGroups
            .filter(
                lineItemGroup =>
                    !lineItemGroup.lineItems
                        .map(lineItem => entityMap.get(lineItem.entityId) as InvoiceListItemDto)
                        .filter(Boolean)
                        .some(lineItem => lineItem.type === InvoiceType.Deposit),
            )
            .map(
                lineItemGroup =>
                    getGroupPrice(
                        lineItemGroup,
                        getVatType(
                            lineItemGroup.vatType ||
                                meta.data?.files?.find(file => file.file.id === lineItemGroup.fileId)?.file.vatType ||
                                defaultVatType?.id,
                        ),
                    ).total,
            )
            .reduce((a, b) => a + b, 0)

        return result
    }, [values?.lineItemGroups, entityMap, defaultVatType])

    const getUpdatedDepositPrices: () => Map<string, PriceVat> = useCallback(() => {
        let unSettledPrice = toSettlePrice

        const entries: [string, PriceVat][] = values?.lineItemGroups
            .flatMap(lineItemGroup => {
                const depositItems = lineItemGroup.lineItems
                    .filter(lineItem => lineItem.type === LineItemType.Invoice)
                    .map(lineItem => entityMap.get(lineItem.entityId) as InvoiceListItemDto)
                    .filter(Boolean)
                    .filter(lineItem => lineItem.type === InvoiceType.Deposit)
                const sumDepositItems = depositItems.map(item => getUnsettledTotalPriceInInvoice(item)).reduce((a, b) => a + b, 0)
                let maxGroupPrice =
                    lineItemGroup.precedence === PricePrecedence.Inherited
                        ? Math.min(unSettledPrice, sumDepositItems)
                        : getGroupPrice(
                              lineItemGroup,
                              getVatType(
                                  lineItemGroup.vatType ||
                                      meta.data?.files?.find(file => file.file.id === lineItemGroup.fileId)?.file.vatType ||
                                      defaultVatType?.id,
                              ),
                          )?.total * -1

                return depositItems.map(lineItem => {
                    const invoiceLineItemTotal = getUnsettledTotalPriceInInvoice(lineItem)
                    const factor = Math.min(maxGroupPrice / invoiceLineItemTotal, 1)
                    const invoiceLineItemNewTotal = invoiceLineItemTotal * factor

                    unSettledPrice = unSettledPrice - invoiceLineItemNewTotal
                    maxGroupPrice = maxGroupPrice - invoiceLineItemTotal

                    return {
                        id: lineItem.id,
                        price: getPriceVat(
                            -invoiceLineItemNewTotal,
                            getVatType(
                                lineItemGroup.vatType ||
                                    meta.data?.files?.find(file => file.file.id === lineItemGroup.fileId)?.file.vatType ||
                                    defaultVatType?.id,
                            ),
                            currency,
                            PricePrecedence.Total,
                        ),
                    }
                })
            })
            .map(item => [item.id, item.price])

        return new Map(entries)
    }, [values?.lineItemGroups, toSettlePrice, entityMap])

    const getUnsettledTotalPriceInInvoice = useCallback(
        (item: InvoiceListItemDto): number => {
            const itemPrice = parseNumber(item.price.total, 'en')
            const unsettledItemPrice = parseNumber(item.unsettledPrice?.total ?? 0, 'en')
            let unsettledPrice = unsettledItemPrice ?? itemPrice
            if (valuesMap.has(item.id)) {
                unsettledPrice = unsettledPrice - parseNumber(valuesMap.get(item.id).price?.total ?? 0, 'en')
            }

            return unsettledPrice
        },
        [values?.lineItemGroups, valuesMap],
    )

    return {
        selection,
        orphans,
        hasOrphans,
        entityMap,
        entityTypeMap,
        map,
        findQuantity,
        getPrice,
        getGroupPrice,
        getTotalTime,
        getTotalDistance,
        toSettlePrice,
        getUpdatedDepositPrices,
    }
}
